import { Check, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import type { BadgeTone } from './surface';

/* -------------------------------------------------------------- ProgressBar */

const barTone: Record<'brand' | 'success' | 'warning' | 'danger' | 'rehab', string> = {
  brand: 'bg-brand',
  success: 'bg-success-solid',
  warning: 'bg-warning-solid',
  danger: 'bg-danger-solid',
  rehab: 'bg-rehab-solid',
};

export function ProgressBar({
  value,
  max = 100,
  label,
  tone = 'brand',
  showValue,
  className,
}: {
  value: number;
  max?: number;
  label: string;
  tone?: keyof typeof barTone;
  showValue?: boolean;
  className?: string;
}) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={className}>
      {showValue ? (
        <div className="mb-1 flex justify-between text-sm">
          <span className="text-muted">{label}</span>
          <span className="font-medium tabular-nums text-fg">{Math.round(pct)}%</span>
        </div>
      ) : null}
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={Math.round(value)}
        className="h-2 overflow-hidden rounded-full bg-sunken"
      >
        <div className={cn('h-full rounded-full transition-[width] duration-(--dur-slow) ease-(--ease-standard)', barTone[tone])} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Stepper */

export type StepStatus = 'completed' | 'current' | 'pending' | 'locked';

export interface StepItem {
  id: string;
  label: string;
  description?: ReactNode;
  status: StepStatus;
}

const statusText: Record<StepStatus, string> = {
  completed: 'Completed',
  current: 'Current',
  pending: 'Pending',
  locked: 'Locked',
};

/** Phase stepper: rehabilitation phases, setup wizard steps. Status is shown as icon + text, never colour alone. */
export function Stepper({
  steps,
  label,
  orientation = 'horizontal',
  className,
  onStepClick,
}: {
  steps: StepItem[];
  label: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  onStepClick?: (id: string) => void;
}) {
  const horizontal = orientation === 'horizontal';
  return (
    <ol aria-label={label} className={cn(horizontal ? 'flex items-start' : 'flex flex-col', className)}>
      {steps.map((step, i) => {
        const last = i === steps.length - 1;
        const clickable = !!onStepClick && step.status !== 'locked';
        const circle = (
          <span
            className={cn(
              'grid size-8 shrink-0 place-items-center rounded-full border-2 text-sm font-semibold transition-colors',
              step.status === 'completed' && 'border-success-solid bg-success-solid text-white',
              step.status === 'current' && 'border-brand bg-surface text-brand-text ring-4 ring-brand-subtle',
              step.status === 'pending' && 'border-border-strong bg-surface text-muted',
              step.status === 'locked' && 'border-border bg-sunken text-disabled',
            )}
          >
            {step.status === 'completed' ? <Check className="size-4" aria-hidden /> : step.status === 'locked' ? <Lock className="size-3.5" aria-hidden /> : i + 1}
          </span>
        );
        const text = (
          <span className={cn('min-w-0', horizontal ? 'mt-2 block text-center' : 'ml-3 block pb-6')}>
            <span className={cn('block text-base font-medium', step.status === 'locked' ? 'text-subtle' : 'text-fg')}>{step.label}</span>
            <span className={cn('block text-xs', step.status === 'current' ? 'font-medium text-brand-text' : 'text-muted')}>
              <span className="sr-only">Status: </span>
              {statusText[step.status]}
            </span>
            {step.description ? <span className="mt-0.5 block text-xs text-muted">{step.description}</span> : null}
          </span>
        );
        const inner = clickable ? (
          <button type="button" onClick={() => onStepClick(step.id)} className={cn('rounded-md text-left', horizontal ? 'flex flex-col items-center' : 'flex')}>
            {circle}
            {text}
          </button>
        ) : (
          <div className={cn(horizontal ? 'flex flex-col items-center' : 'flex')}>
            {circle}
            {text}
          </div>
        );
        return (
          <li
            key={step.id}
            aria-current={step.status === 'current' ? 'step' : undefined}
            className={cn('relative', horizontal ? 'flex-1' : 'flex')}
          >
            {!last ? (
              <span
                aria-hidden
                className={cn(
                  'absolute bg-border-strong',
                  horizontal ? 'left-[calc(50%+1.25rem)] right-[calc(-50%+1.25rem)] top-4 h-0.5' : 'left-4 top-9 -ml-px h-[calc(100%-2rem)] w-0.5',
                  step.status === 'completed' && 'bg-success-solid',
                )}
              />
            ) : null}
            {inner}
          </li>
        );
      })}
    </ol>
  );
}

/* ----------------------------------------------------------------- Timeline */

export interface TimelineEventData {
  id: string;
  time: string;
  title: ReactNode;
  description?: ReactNode;
  actor?: string;
  icon?: ReactNode;
  tone?: BadgeTone;
  /** Optional trailing element (badge, link). */
  aside?: ReactNode;
}

export interface TimelineGroupData {
  label: string;
  events: TimelineEventData[];
}

const dotTone: Record<BadgeTone, string> = {
  neutral: 'bg-sunken text-muted border-border',
  brand: 'bg-brand-subtle text-brand-text border-brand-border',
  success: 'bg-success-bg text-success-fg border-success-border',
  warning: 'bg-warning-bg text-warning-fg border-warning-border',
  danger: 'bg-danger-bg text-danger-fg border-danger-border',
  info: 'bg-info-bg text-info-fg border-info-border',
  rehab: 'bg-rehab-bg text-rehab-fg border-rehab-border',
};

/** Chronological events grouped by day (TODAY / YESTERDAY / date). Any module can contribute events. */
export function Timeline({ groups, className }: { groups: TimelineGroupData[]; className?: string }) {
  return (
    <div className={cn('space-y-7', className)}>
      {groups.map((group) => (
        <section key={group.label} aria-label={group.label}>
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-subtle uppercase">{group.label}</h3>
          <ol>
            {group.events.map((event, i) => (
              <li key={event.id} className="relative flex gap-3 pb-5 last:pb-0">
                {i < group.events.length - 1 ? <span aria-hidden className="absolute bottom-0 left-[3.9rem] top-8 w-px bg-border" /> : null}
                <time className="w-11 shrink-0 pt-1 text-sm tabular-nums text-muted">{event.time}</time>
                <span className={cn('z-[1] grid size-8 shrink-0 place-items-center rounded-full border [&_svg]:size-4', dotTone[event.tone ?? 'neutral'])}>{event.icon}</span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-base font-medium text-fg">{event.title}</p>
                    {event.aside}
                  </div>
                  {event.description ? <p className="mt-0.5 text-sm text-muted">{event.description}</p> : null}
                  {event.actor ? <p className="mt-0.5 text-xs text-subtle">{event.actor}</p> : null}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
